import { parseWalletAmount } from './parseWalletAmount.js';

function round2(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Bill and recharge dates arrive as d-m-Y or Y-m-d. */
export function parsePurchaseDate(value) {
  if (!value) {
    return null;
  }
  const text = String(value).trim();
  let match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  match = text.match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (!match) {
    return null;
  }
  return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
}

function todayIso() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function monthEnd(monthKey) {
  const [year, month] = monthKey.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${monthKey}-${String(lastDay).padStart(2, '0')}`;
}

function purchaseAmount(row) {
  return parseWalletAmount(row?.totalbillamount ?? row?.billamount);
}

/**
 * Ended calendar months before today.
 * The month total picks achiever or non-achiever. That percent applies to every
 * shop, mobile, DTH, and credit-card purchase up to the end of that month.
 */
export function calculateApmMonthlyBonus(bills, mileStoneDetails) {
  const stone = mileStoneDetails?.mile_stone || {};
  const target = parseWalletAmount(stone.target_amount ?? mileStoneDetails?.target_amount);
  const completed = parseWalletAmount(stone.percentage_completed);
  const normal = parseWalletAmount(stone.percentage_normal);
  if (!Array.isArray(bills) || bills.length === 0 || (completed === 0 && normal === 0)) {
    return 0;
  }

  const today = todayIso();
  const purchases = bills
    .map((row) => ({
      date: parsePurchaseDate(row?.date_time || row?.created_at),
      amount: purchaseAmount(row),
    }))
    .filter((row) => row.date);

  const monthKeys = [...new Set(purchases.map((row) => row.date.slice(0, 7)))]
    .filter((key) => monthEnd(key) < today)
    .sort();

  let totalBonus = 0;
  monthKeys.forEach((key) => {
    const end = monthEnd(key);
    const start = `${key}-01`;
    const monthPurchase = purchases
      .filter((row) => row.date >= start && row.date <= end)
      .reduce((sum, row) => sum + row.amount, 0);
    const totalPurchase = purchases
      .filter((row) => row.date <= end)
      .reduce((sum, row) => sum + row.amount, 0);
    const percent = target > 0 && monthPurchase >= target ? completed : normal;
    totalBonus += round2((totalPurchase / 100) * percent);
  });

  return round2(totalBonus);
}

export async function applyApmMonthlyBonus({ axios, siteUrl, token, data }) {
  if (!data || data.apm_bonus_calculated) {
    return data;
  }

  try {
    const res = await axios.get(`${siteUrl}/customer/instance`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    const calculated = calculateApmMonthlyBonus(res.data?.data || [], data.mile_stone_details);
    const earned = parseWalletAmount(data.apm_bonus);
    data.apm_bonus = round2(Math.max(earned, calculated));
  } catch (err) {
    return data;
  }

  return data;
}
